import { useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useFetch } from "../../components/crm/ui";
import { ShieldCheck, Calendar } from "lucide-react";

export default function FsmWarranty() {
  const { data: materials } = useFetch("/materials");
  const { data: customers } = useFetch("/customers");
  const [form, setForm] = useState({
    material_id: "",
    serial_code: "",
    customer_id: "",
    commissioning_date: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  const selectedMaterial = useMemo(
    () => materials.find((m) => m.id === form.material_id),
    [materials, form.material_id]
  );
  const warrantyDays = selectedMaterial?.warranty_days || 0;
  const expiryPreview = useMemo(() => {
    if (!form.commissioning_date || !warrantyDays) return null;
    const d = new Date(form.commissioning_date);
    d.setDate(d.getDate() + warrantyDays);
    return d.toISOString().slice(0, 10);
  }, [form.commissioning_date, warrantyDays]);

  async function submit(e) {
    e.preventDefault();
    if (!form.serial_code.trim()) {
      toast.error("Serial code is mandatory at commissioning");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        material_id: form.material_id,
        serial_code: form.serial_code.trim(),
        customer_id: form.customer_id,
        commissioning_date: new Date(form.commissioning_date).toISOString(),
      };
      await api.post("/warranties", payload);
      toast.success("Equipment registered into installed base");
      setForm({
        material_id: "", serial_code: "", customer_id: "",
        commissioning_date: new Date().toISOString().slice(0, 10),
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  const inp =
    "w-full bg-[#0A1128] border border-[#1E293B] focus:border-[#FF5C00] outline-none px-3 py-3 text-sm font-mono text-white";

  return (
    <div className="px-5 py-6 space-y-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">Field Action / Commissioning</div>
      <h2 className="font-display text-3xl font-bold tracking-tighter">Register equipment.</h2>
      <p className="text-sm text-[#94A3B8]">
        Capture the serial code at commissioning. Warranty period is read from the material master automatically.
      </p>

      <form onSubmit={submit} className="space-y-4 mt-4" data-testid="warranty-form">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1.5">Material *</div>
          <select
            required
            value={form.material_id}
            onChange={(e) => setForm({ ...form, material_id: e.target.value })}
            className={inp}
            data-testid="warranty-material"
          >
            <option value="">— Select —</option>
            {materials.map((m) => (
              <option key={m.id} value={m.id}>
                {`${m.code} · ${m.name}`}
              </option>
            ))}
          </select>
          {selectedMaterial && (
            <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#D4FF00]">
              ⚡ Warranty from material master: {warrantyDays} days
              {warrantyDays > 0 && <> ({(warrantyDays / 365).toFixed(1)} years)</>}
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1.5">Serial code *</div>
          <input
            required
            value={form.serial_code}
            onChange={(e) => setForm({ ...form, serial_code: e.target.value })}
            className={inp}
            data-testid="warranty-serial"
            placeholder="Mandatory at commissioning"
          />
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1.5">Customer *</div>
          <select
            required
            value={form.customer_id}
            onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
            className={inp}
            data-testid="warranty-customer"
          >
            <option value="">— Select —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.customer_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mb-1.5">Commissioning date *</div>
          <input
            type="date"
            required
            value={form.commissioning_date}
            onChange={(e) => setForm({ ...form, commissioning_date: e.target.value })}
            className={inp}
          />
        </div>

        {selectedMaterial && expiryPreview && (
          <div className="border border-[#1E293B] bg-[#0A1128] p-4 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[#D4FF00] mt-0.5" />
            <div className="text-xs font-mono">
              <div className="text-[#94A3B8] uppercase tracking-widest text-[10px]">Computed expiry</div>
              <div className="text-white text-base mt-1 flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                {new Date(expiryPreview).toLocaleDateString()}
              </div>
              <div className="text-[#94A3B8] mt-1">
                Status will be auto-derived from material master & current date.
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          data-testid="warranty-submit"
          className="btn-tactile w-full bg-[#FF5C00] text-white py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#D4FF00] hover:text-[#0A1128] transition-colors"
        >
          {saving ? "Registering…" : "Register equipment"}
        </button>
      </form>
    </div>
  );
}
